import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Separator } from "../../components/ui/separator"
import AttorneyInformationCard from "../../components/forms/AttorneyInformationCard"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { attorneysApi } from "../../services/attorneysApi"
import { AvailabilitySwitch, AVAILABLE_FOR_LEADS_LABEL } from "../../components/attorneys/AvailabilitySwitch"

interface AttorneyDetails {
  id: string
  first_name: string
  last_name: string
  name: string
  email: string
  phone: string
  voicemail_ending?: string | null
  available?: boolean
  created_at?: string
  updated_at?: string
}

export default function AttorneyDetail() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [attorneyDetails, setAttorneyDetails] = useState<AttorneyDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editingData, setEditingData] = useState<AttorneyDetails | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [deleteAttorneyModal, setDeleteAttorneyModal] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [editErrors, setEditErrors] = useState<{ [key: string]: string[] }>({})
  
  // Phone validation states
  const [phoneChecking, setPhoneChecking] = useState(false)
  const [phoneAvailable, setPhoneAvailable] = useState<boolean | null>(null)
  const [phoneMessage, setPhoneMessage] = useState("")
  const [phoneFormatError, setPhoneFormatError] = useState(false)

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState("")

  const fetchAttorneyDetails = useCallback(async (attorneyId: string) => {
    try {
      setLoading(true)
      const response = await attorneysApi.getAttorney(attorneyId)
      
      // Check if response has the expected structure
      if (response && response.attorney) {
        setAttorneyDetails(response.attorney)
      } else if (response && typeof response === 'object' && 'id' in response && 'first_name' in response) {
        // If the response is the attorney object directly
        setAttorneyDetails(response as unknown as AttorneyDetails)
      } else {
        console.error('Unexpected API response structure:', response)
        setAttorneyDetails(null)
      }
    } catch (error) {
      console.error('Failed to fetch attorney details:', error)
      setAttorneyDetails(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (state?.attorneyId) {
      fetchAttorneyDetails(state.attorneyId)
    } else {
      setAttorneyDetails(null)
      setLoading(false)
    }
  }, [state?.attorneyId, fetchAttorneyDetails])

  // Debounced email availability check for editing
  useEffect(() => {
    if (!isEditing || !editingData?.email || editingData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // First validate email format
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    
    if (!emailRegex.test(editingData.email)) {
      setEmailAvailable(null)
      setEmailMessage("Please enter a valid email address")
      return
    }

    // Additional email format checks
    const [localPart, domain] = editingData.email.split('@')
    if (localPart.length > 64) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot exceed 64 characters")
      return
    }
    if (domain.length > 253) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot exceed 253 characters")
      return
    }
    if (localPart.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot be empty")
      return
    }
    if (domain.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot be empty")
      return
    }

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true)
        const response = await attorneysApi.checkAttorneyEmailAvailability(editingData.email, attorneyDetails?.id)
        setEmailAvailable(response.available)
        setEmailMessage(response.message)
      } catch (error) {
        console.error('Email availability check failed:', error)
        setEmailAvailable(null)
        setEmailMessage("Unable to verify email")
      } finally {
        setEmailChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [editingData?.email, isEditing, attorneyDetails?.id])

  // Debounced phone availability check for editing
  useEffect(() => {
    if (!isEditing || !editingData?.phone || editingData.phone.length < 10) {
      setPhoneAvailable(null)
      setPhoneMessage("")
      return
    }

    // Format validation is now handled by the child component
    // Only check availability here
    const timer = setTimeout(async () => {
      try {
        setPhoneChecking(true)
        const response = await attorneysApi.checkAttorneyPhoneAvailability(editingData.phone, attorneyDetails?.id)
        setPhoneAvailable(response.available)
        setPhoneMessage(response.message)
      } catch (error) {
        console.error('Phone availability check failed:', error)
        setPhoneAvailable(null)
        setPhoneMessage("Unable to verify phone number")
      } finally {
        setPhoneChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [editingData?.phone, isEditing, attorneyDetails?.id])



  const handleBack = () => {
    navigate("/app/attorneys")
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditingData(attorneyDetails)
    // Reset email validation when starting to edit
    setEmailAvailable(null)
    setEmailMessage("")
    // Reset phone validation when starting to edit
    setPhoneAvailable(null)
    setPhoneMessage("")
    // Reset phone format error when starting to edit
    setPhoneFormatError(false)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingData(null)
    setEditErrors({})
    // Reset email validation when canceling edit
    setEmailAvailable(null)
    setEmailMessage("")
    // Reset phone validation when canceling edit
    setPhoneAvailable(null)
    setPhoneMessage("")
    // Reset phone format error when canceling edit
    setPhoneFormatError(false)
  }

  const updateAvailability = async (available: boolean) => {
    if (!attorneyDetails) {
      return
    }
    const previous = attorneyDetails.available
    setAttorneyDetails({ ...attorneyDetails, available })
    try {
      const response = await attorneysApi.updateAttorney(attorneyDetails.id, { available })
      if (response.attorney) {
        setAttorneyDetails(response.attorney)
      }
    } catch (error) {
      console.error("Failed to update availability:", error)
      setAttorneyDetails({ ...attorneyDetails, available: previous })
    }
  }

  const handleSaveEdit = () => {
    if (!editingData) return
    
    // Validate required fields with same logic as LeadDetails.tsx
    const errors: { [key: string]: string[] } = {}
    
    // First name validation
    if (!editingData.first_name?.trim()) {
      errors.first_name = ["First name is required"]
    } else if (editingData.first_name.length > 50) {
      errors.first_name = ["First name cannot exceed 50 characters"]
    }
    
    // Last name validation
    if (!editingData.last_name?.trim()) {
      errors.last_name = ["Last name is required"]
    } else if (editingData.last_name.length > 50) {
      errors.last_name = ["Last name cannot exceed 50 characters"]
    }
    
    // Email validation (required)
    if (!editingData.email?.trim()) {
      errors.email = ["Email is required"]
    } else if (editingData.email.length > 255) {
      errors.email = ["Email cannot exceed 255 characters"]
    } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(editingData.email)) {
      errors.email = ["Invalid email format"]
    }
    
    // Phone validation
    if (!editingData.phone?.trim()) {
      errors.phone = ["Phone number is required"]
    } else {
      // Remove + if present and validate it's all numbers
      const phoneWithoutPlus = editingData.phone.startsWith('+') ? editingData.phone.slice(1) : editingData.phone
      if (!/^\d+$/.test(phoneWithoutPlus)) {
        errors.phone = ["Phone number must contain only numbers (with optional + prefix)"]
      }
    }

    const ending = (editingData.voicemail_ending || "").trim()
    if (ending && !/^\d{4}$/.test(ending)) {
      errors.voicemail_ending = ["Voicemail ending must be exactly 4 digits"]
    }
    
         // Check email and phone availability before allowing save
     if (emailAvailable !== true) {
       errors.email = ["Please ensure the email is available before submitting"]
     }
     if (phoneAvailable !== true) {
       errors.phone = ["Please ensure the phone number is available before submitting"]
     }
     
     // Check phone format validation
     if (phoneFormatError) {
       errors.phone = ["Please fix phone number format errors before submitting"]
     }
    
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      return
    }
    
    // Clear errors if validation passes
    setEditErrors({})
    setShowSaveDialog(true)
  }

  const confirmSave = async () => {
    if (!editingData || !attorneyDetails) {
      setShowSaveDialog(false)
      return
    }

    try {
      // Prepare the data for the API call (exclude id and timestamps)
      const updateData = {
        first_name: editingData.first_name,
        last_name: editingData.last_name,
        email: editingData.email,
        phone: editingData.phone,
        voicemail_ending: (editingData.voicemail_ending || "").trim() || null,
      }

      // Make the API call to update the attorney
      const response = await attorneysApi.updateAttorney(attorneyDetails.id, updateData)
            
      // Update the local state with the response data
      if (response.attorney) {
        setAttorneyDetails(response.attorney)
      } else {
        // Fallback: update with editing data
        setAttorneyDetails(editingData)
      }
      
      // Reset editing state
      setIsEditing(false)
      setEditingData(null)
      setShowSaveDialog(false)
      setEditErrors({})
      // Reset email validation
      setEmailAvailable(null)
      setEmailMessage("")
      // Reset phone validation
      setPhoneAvailable(null)
      setPhoneMessage("")
      // Reset phone format error
      setPhoneFormatError(false)
      
    } catch (error) {
      console.error('Failed to update attorney:', error)
      // Handle API errors - you might want to show a toast notification here
      // For now, we'll keep the dialog open and let the user try again
    }
  }

  const cancelSave = () => {
    setShowSaveDialog(false)
  }



  const deleteAttorneyModalClose = () => {
    setDeleteAttorneyModal(false)
    setSelectedId("")
  }

  const deleteItem = async () => {
    try {
      await attorneysApi.deleteAttorney(selectedId)
      deleteAttorneyModalClose()
      navigate("/app/attorneys")
    } catch (error) {
      console.error('Failed to delete attorney:', error)
    }
  }

  const handleDataChange = (field: string, value: any) => {
    if (editingData) {
      setEditingData({ ...editingData, [field]: value })
      // Clear error for this field when user starts typing
      if (editErrors[field]) {
        setEditErrors(prev => ({ ...prev, [field]: [] }))
      }
      
      // Reset validation states when email or phone changes
      if (field === 'email') {
        setEmailAvailable(null)
        setEmailMessage("")
      } else if (field === 'phone') {
        setPhoneAvailable(null)
        setPhoneMessage("")
        // Reset phone format error when phone changes
        setPhoneFormatError(false)
      }
    }
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading attorney details...</p>
        </div>
      </div>
    )
  }

  if (!attorneyDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Attorney not found</p>
          <Button onClick={handleBack} className="mt-4">
            Back to Attorneys
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleBack} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Back to Attorneys</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-2xl font-bold text-slate-900">Attorney Details</h1>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">{AVAILABLE_FOR_LEADS_LABEL}</span>
                <AvailabilitySwitch
                  checked={attorneyDetails.available === true}
                  onChange={updateAvailability}
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Attorney Information */}
          <AttorneyInformationCard
            attorneyData={{
              first_name: editingData ? editingData.first_name : attorneyDetails.first_name,
              last_name: editingData ? editingData.last_name : attorneyDetails.last_name,
              email: editingData ? editingData.email : attorneyDetails.email,
              phone: editingData ? editingData.phone : attorneyDetails.phone,
              voicemail_ending: editingData
                ? editingData.voicemail_ending
                : attorneyDetails.voicemail_ending,
            }}
            errors={editErrors}
            onDataChange={handleDataChange}
            viewOnly={!isEditing}
            isEditing={isEditing}
            onEdit={handleEdit}
            onSave={() => handleSaveEdit()}
            onCancel={handleCancelEdit}
            emailValidation={isEditing ? {
              emailChecking,
              emailAvailable,
              emailMessage
            } : undefined}
            phoneValidation={isEditing ? {
              phoneChecking,
              phoneAvailable,
              phoneMessage
            } : undefined}
            onPhoneFormatValidationChange={setPhoneFormatError}
          />
        </div>

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogDescription>
                Are you sure you want to save the changes to this attorney? This will update the attorney information permanently.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={cancelSave}>
                Cancel
              </Button>
              <Button onClick={confirmSave} className="bg-green-600 hover:bg-green-700">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DeleteModal
          open={deleteAttorneyModal}
          onClose={deleteAttorneyModalClose}
          onConfirm={deleteItem}
          title="Delete Attorney"
          description="Are you sure you want to delete this attorney? This action cannot be undone."
        />
      </div>
    </div>
  )
} 